import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useRemoveWidget } from '../../hooks/useDashboard';
import WidgetContent from './WidgetContent';
import { GripVertical, X, ChevronUp, ChevronDown, Maximize2, RefreshCw, AlertCircle } from 'lucide-react';
import { Modal } from 'antd';
import { toast } from '@rickylandino/react-messages';
import Globals from '../../config/globals';

/**
 * DashboardWidget component
 * Individual draggable widget with controls
 */
export default function DashboardWidget({ widget, editMode }) {
const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver
} = useSortable({
    id: widget.widgetInstanceKey,
    disabled: false
});

    const removeWidget = useRemoveWidget();
    const initialCollapsed = widget.isCollapsed !== undefined ? widget.isCollapsed : true;
    const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isWidgetLoading, setIsWidgetLoading] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [sharedState, setSharedState] = useState({ 
        isCollapsed: initialCollapsed,
        isExpanded: false,
        setIsLoading: setIsWidgetLoading,
        refetch: null
    });

    // Detect if this is a system widget (cannot be deleted)
    const isSystemWidget = widget.widgetDefinition?.widgetCategory === 'system';
    const effectiveCollapsed = isSystemWidget ? isCollapsed : false;


    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 'auto'
    };

    const handleRemoveClick = () => {
        // Only widgets with a valid ID can be removed
        if (!widget.userDashboardWidgetId) {
            console.warn('Cannot remove widget without valid ID');
            toast.error('Unable to remove this widget');
            return;
        }
        setDeleteModalVisible(true);
    };

    const handleConfirmDelete = () => {
        if (widget.userDashboardWidgetId) {
            removeWidget.mutate(widget.userDashboardWidgetId, {
                onSuccess: () => {
                    toast.success('Widget removed from dashboard');
                    setDeleteModalVisible(false);
                },
                onError: (error) => {
                    console.error('Failed to remove widget:', error);
                    toast.error('Failed to remove widget');
                    setDeleteModalVisible(false);
                },
            });
        }
    };

    const handleCancelDelete = () => {
        setDeleteModalVisible(false);
    };

    const handleToggleCollapse = () => {
        const newCollapsed = !isCollapsed;
        setIsCollapsed(newCollapsed);
        setSharedState(prev => ({ ...prev, isCollapsed: newCollapsed }));
        // Optionally save to backend
        // dashboardService.toggleCollapsed(widget.userDashboardWidgetId, newCollapsed);
    };

    const handleExpand = () => {
        const newExpanded = !isExpanded;
        setIsExpanded(newExpanded);
        setSharedState(prev => ({ ...prev, isExpanded: newExpanded }));
        
        // If closing expanded view, reset widget loading state
        if (!newExpanded) {
            setIsWidgetLoading(false);
        }
    };

    const handleRefresh = () => {
        if (sharedState.refetch && typeof sharedState.refetch === 'function') {
            sharedState.refetch();
        }
    };

    return (
        <>
            {/* Normal widget card */}
            <div className={isExpanded ? 'hidden' : 'relative'}>
                {/* Drop indicator - shows where the dragged widget will be inserted */}
                {isOver && editMode && (
                    <div className="absolute -top-2 left-0 right-0 h-1 bg-pink-500 dark:bg-pink-400 rounded-full shadow-lg shadow-pink-500/50 dark:shadow-pink-400/50 z-10 animate-pulse" />
                )}
                
                <div
                    ref={setNodeRef}
                    style={style}
                    {...attributes}
                    className="rounded-xl"
                >
                    <div 
                        className="rounded-xl h-full border-2 shadow-md transition-all duration-200"
                        style={{
                            backgroundColor: Globals.theme === 'dark' ? '#2b2b2b' : '#fdf2f8',
                            borderColor: Globals.theme === 'dark' ? '#ec4899' : '#f9a8d4',
                            boxShadow: Globals.theme === 'dark' 
                                ? '0 0 0 1px rgba(236, 72, 153, 0.3)' 
                                : '0 4px 6px -1px rgba(236, 72, 153, 0.1), 0 2px 4px -2px rgba(236, 72, 153, 0.05)'
                        }}
                    >
                        {/* Card Header */}
                        <div 
                            className="px-4 py-3 border-b bg-transparent"
                            style={{
                                borderColor: Globals.theme === 'dark' ? '#3f3f46' : '#fbcfe8'
                            }}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                    {editMode && (
                                        <button
                                            {...listeners}
                                            className="cursor-grab cursor-move active:cursor-grabbing"
                                            style={{
                                                color: Globals.theme === 'dark' ? '#a1a1aa' : '#ec4899'
                                            }}
                                        >
                                            <GripVertical className="w-5 h-5" />
                                        </button>
                                    )}
                                    <h3 
                                        className="text-lg font-semibold truncate"
                                        style={{
                                            color: Globals.theme === 'dark' ? 'white' : '#9f1239'
                                        }}
                                    >
                                        {widget.widgetTitle || widget.widgetDefinition?.widgetName || 'Untitled Widget'}
                                    </h3>
                                </div>
                                <div className="flex items-center gap-1">
                                    {isSystemWidget && sharedState.refetch && (
                                        <button
                                            onClick={handleRefresh}
                                            className="p-1 rounded transition-colors"
                                            style={{
                                                color: Globals.theme === 'dark' ? '#d4d4d8' : '#ec4899'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#3f3f46' : '#fce7f3'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            title="Refresh widget data"
                                        >
                                            <RefreshCw className="w-4 h-4" />
                                        </button>
                                    )}
                                    {isSystemWidget && (
                                        <button
                                            onClick={handleExpand}
                                            className="p-1 rounded transition-colors"
                                            style={{
                                                color: Globals.theme === 'dark' ? '#d4d4d8' : '#ec4899'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#3f3f46' : '#fce7f3'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            title="Expand widget"
                                        >
                                            <Maximize2 className="w-4 h-4" />
                                        </button>
                                    )}
                                    {isSystemWidget && (
                                        <button
                                            onClick={handleToggleCollapse}
                                            className="p-1 rounded transition-colors"
                                            style={{
                                                color: Globals.theme === 'dark' ? '#d4d4d8' : '#ec4899'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#3f3f46' : '#fce7f3'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            title={isCollapsed ? 'Expand' : 'Collapse'}
                                        >
                                            {isCollapsed ? (
                                                <ChevronDown className="w-4 h-4" />
                                            ) : (
                                                <ChevronUp className="w-4 h-4" />
                                            )}
                                        </button>
                                    )}
                                    {/* Remove button only for user widgets (not system widgets) */}
                                    {!isSystemWidget && widget.userDashboardWidgetId && (
                                        <button
                                            onClick={handleRemoveClick}
                                            className="p-1 rounded transition-colors"
                                            style={{
                                                color: Globals.theme === 'dark' ? '#f87171' : '#dc2626'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#7f1d1d' : '#fee2e2'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            title="Remove widget"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="p-2">
                        <WidgetContent 
                            widget={widget} 
                            sharedState={{ ...sharedState, isCollapsed: effectiveCollapsed }}
                            setSharedState={setSharedState}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Expanded fullscreen overlay */}
            {isExpanded && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center animate-in fade-in duration-200"
                    onClick={handleExpand}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Escape' && handleExpand()}
                    aria-label="Close expanded view"
                >
                    {/* Glassmorphism backdrop */}
                    <div 
                        className="absolute inset-0 bg-gradient-to-br from-pink-500/10 via-transparent to-pink-600/5 backdrop-blur-xl"
                        style={{ backgroundColor: 'rgba(219, 39, 119, 0.15)' }}
                    />

                    <div
                        className="relative z-10 w-[90vw] max-w-7xl h-[90vh] bg-white/95 dark:bg-[#2b2b2b]/95 backdrop-blur-sm rounded-2xl shadow-2xl border-2 border-pink-200/50 dark:border-pink-500/80 flex flex-col animate-in zoom-in-95 duration-200"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        {/* Loading Overlay */}
                        {isWidgetLoading && (
                            <div 
                                className="absolute inset-0 flex items-center justify-center rounded-2xl z-20"
                                style={{
                                    backgroundColor: Globals.theme === 'dark' ? 'rgba(43, 43, 43, 0.9)' : 'rgba(255, 255, 255, 0.9)'
                                }}
                            >
                                <div className="flex flex-col items-center gap-3">
                                    <div 
                                        className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
                                        style={{ 
                                            borderColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777',
                                            borderTopColor: 'transparent'
                                        }}
                                    />
                                    <span 
                                        className="text-sm font-medium"
                                        style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                                    >
                                        Loading widget data...
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="flex items-center justify-between px-8 py-6 border-b border-zinc-200 dark:border-zinc-700 bg-transparent">
                            <h2 className="text-2xl font-bold text-zinc-900 dark:text-white">
                                {widget.widgetTitle || widget.widgetDefinition?.widgetName || 'Untitled Widget'}
                            </h2>
                            <div className="flex items-center gap-2">
                                {sharedState.refetch && (
                                    <button
                                        onClick={handleRefresh}
                                        className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors"
                                        title="Refresh widget data"
                                    >
                                        <RefreshCw className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
                                    </button>
                                )}
                                <button
                                    onClick={handleExpand}
                                    className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors"
                                >
                                    <X className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-auto px-8 py-6">
                            <WidgetContent 
                                widget={widget} 
                                sharedState={sharedState}
                                setSharedState={setSharedState}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Widget Confirmation Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <AlertCircle 
                            style={{ 
                                color: '#dc2626',
                                width: '20px',
                                height: '20px'
                            }} 
                        />
                        <span>Remove Widget from Dashboard</span>
                    </div>
                }
                visible={deleteModalVisible}
                onOk={handleConfirmDelete}
                onCancel={handleCancelDelete}
                okText="Remove Widget"
                cancelText="Cancel"
                okButtonProps={{
                    danger: true,
                    style: {
                        backgroundColor: '#dc2626',
                        borderColor: '#dc2626'
                    }
                }}
            >
                <div>
                    <p style={{ marginBottom: '12px' }}>
                        This widget will be permanently removed from your dashboard. You can add it back later if you choose.
                    </p>
                    <p style={{ fontWeight: 500 }}>
                        Are you sure you want to continue?
                    </p>
                </div>
            </Modal>
        </>
    );
}
